import './Glossary.scss'
import { useState, useCallback } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useRestClient } from '@/contexts/QueryProvider'
import { Loading } from '@/ui-legacy'
import { useSessionStorage } from '@/hooks'
import { isSuperUser, isApplicationAdmin } from '@/util'
import { MDXEditorComponent } from '@/components/form-components/MDXEditorComponent'
import { Button } from '@/components/shadcn/button'

type GlossaryProps = {
  appId: number | null
}

const Glossary = ({ appId }: GlossaryProps) => {
  const { getMarkdownApiGetGlossary, postMarkdownApiSaveGlossary } = useRestClient()
  const { data, isPending, isError, refetch } = getMarkdownApiGetGlossary.useQuery(
    { path: { applicationId: appId! } },
    { enabled: appId != null }
  )
  const { mutateAsync: saveGlossary, isPending: isSaving } = postMarkdownApiSaveGlossary.useMutation({
    path: { applicationId: appId! },
  })

  const [roles] = useSessionStorage('roles', 0)
  const canEdit = isSuperUser(roles) || isApplicationAdmin(roles)

  const content = data?.content ?? ''
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')

  const openEditing = useCallback(() => {
    setEditValue(content)
    setIsEditing(true)
  }, [content])

  const cancelEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSave = useCallback(async () => {
    await saveGlossary({ content: editValue })
    await refetch()
    setIsEditing(false)
  }, [saveGlossary, editValue, refetch])

  if (isPending) return <Loading />
  if (isError) return <div className="p-4 text-sm text-muted-foreground">Failed to load Glossary.</div>

  return (
    <div className="ux-glossary-layout">
      {canEdit && (
        <div className="flex items-center justify-end gap-2 border-b px-4 py-2">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={openEditing}>
              Edit
            </Button>
          )}
        </div>
      )}

      {isEditing ? (
        <div className="p-4">
          <MDXEditorComponent
            markdown={editValue}
            onChange={setEditValue}
            contentEditableClassName="min-h-96 bg-background"
            autoFocus
          />
        </div>
      ) : (
        <div className="prose prose-sm max-w-none p-4">
          <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
        </div>
      )}
    </div>
  )
}

export default Glossary
